import { IconName } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button } from '@prio365/prio365-react-library';
import { Alert, Dropdown, Input, Menu, Modal, notification } from 'antd';
import classNames from 'classnames';
import React, {
  MutableRefObject,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useDrag, useDrop } from 'react-dnd';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { useTheme } from 'theming';
import { getMailSettings } from '../../../../apps/main/rootReducer';
import Flex from '../../../../components/Flex';
import { UltimateFilterSearchRef } from '../../../../components/UltimateFilter/createUltimateFilter';
import { DND_TYPE_EMAIL, DND_TYPE_EMAIL_FOLDER } from '../../../../dnd/types';
import { MailFolder } from '../../../../models/MailFolder';
import { MailSearchFormModel } from '../../../../models/MailSearch';
import { Message } from '../../../../models/Message';
import {
  ProjectNewsCategoryKey,
  ProjectNewsDto,
} from '../../../../models/Project';
import { MailFolderId, ProjectId } from '../../../../models/Types';
import { PrioTheme } from '../../../../theme/types';
import { makePrioStyles } from '../../../../theme/utils';
import { folderNameRegex } from '../../../documents/util';
import {
  addOpenMailFolder,
  fetchMailFolders,
} from '../../actions/actionControllers/mailFoldersController';
import { setMailListNavigationState } from '../../actions/actionControllers/mailNavigationActionController';
import { moveMessage } from '../../actions/actionControllers/messageActionController';
import { renameMailFolderMe } from '../../actions/me/mailFoldersMe';
import { renameMailFolder } from '../../actions/projects/mailFolders';
import { fetchMessagesSagaAction } from '../../actions/sagas';
import { SpecialMailFolders } from '../../actions/types';
import { apiMoveMailFolder } from '../../api';
import { getDisplayName } from '../../util';
import { MailFilterContext } from '../MailSearch/MailFilterContext';
import { deleteMailSearchResults } from '../../actions/actionControllers/searchActionController';

const useStyles = makePrioStyles((theme: PrioTheme) => ({
  root: {},
  menuItem: {
    '&.ant-menu-item': {
      paddingRight: 28,
    },
    '&.ant-menu-item-selected': {
      color: '#000',
    },
    borderRadius: 2,
  },
  subMenuItem: {
    '& a': {
      color: '#000',
    },
    '& a:hover': {
      color: theme.old.palette.primaryColor,
      backgroundColor: theme.old.palette.backgroundPalette.hover.sub,
    },
    '& .ant-menu-submenu-title:hover': {
      backgroundColor: theme.old.palette.backgroundPalette.hover.sub,
    },
    '& .ant-menu-submenu-title:hover > span > a > div > .svg-inline--fa': {
      color: theme.old.palette.primaryColor,
    },
    '& .ant-menu-submenu-arrow': {
      marginTop: '2px',
    },
  },
  subMenuItemCompressedView: {
    '& .ant-menu-submenu-arrow': {
      right: '6px',
    },
  },
  subMenuItemMiddleView: {
    '& .ant-menu-submenu-arrow': {
      right: '6px',
    },
  },

  selectedSubMenu: {
    '& .ant-menu-item-selected': {
      backgroundColor: 'transparent!important',
    },
    '&.ant-menu-submenu > div': {
      backgroundColor: theme.old.palette.backgroundPalette.active.sub,
      color: theme.old.palette.primaryColor,
      fontWeight: 600,
      '& a': {
        color: `${theme.old.palette.primaryColor}!important`,
      },
      '& > span > a > div > .svg-inline--fa': {
        color: theme.old.palette.primaryColor,
      },
    },
    '&.ant-menu-submenu-selected > .ant-menu-submenu-title::before': {
      content: '""',
      position: 'absolute',
      width: 2,
      height: '100%',
      left: 0,
      backgroundColor: theme.old.palette.primaryColor,
    },
    '&.ant-menu-submenu-selected': {
      color: '#000',
    },
    '&.ant-menu-submenu-selected > .ant-menu-submenu-title:hover': {
      backgroundColor: theme.old.palette.backgroundPalette.hover.sub,
    },
  },
  selectedMenuItem: {
    color: `${theme.old.palette.primaryColor}!important`,
    '& a': {
      color: `${theme.old.palette.primaryColor}!important`,
    },
    '&.ant-menu-item-selected': {
      color: theme.old.palette.primaryColor,
    },
    '&.ant-menu-item-selected a': {
      color: `${theme.old.palette.primaryColor}!important`,
    },
  },
  collapsedMenuItem: {
    width: 48,
  },
  icon: {
    marginRight: theme.old.spacing.unit(1.5),
    width: 18,
  },
  button: {
    height: 'auto',
    backgroundColor: 'transparent',
    zIndex: 1,
    '&:hover': {
      backgroundColor: 'transparent',
    },
    '& > .prio-button-icon': {
      color: theme.old.typography.colors.base,
    },
    '&:hover > .prio-button-icon': {
      color: theme.old.typography.colors.base,
    },
  },
  activeMenuItem: {
    '&.ant-menu-item-only-child': {
      outline: `1px dashed ${theme.old.palette.primaryColor}`,
      outlineWidth: 2,
      outlineOffset: '-2px',
    },
    '& > div': {
      outline: `1px dashed ${theme.old.palette.primaryColor}`,
      outlineWidth: 2,
      outlineOffset: '-2px',
    },
    '&.ant-menu-item > a': {
      color: theme.old.typography.colors.inverted,
    },
  },
  unreadItemCount: {
    fontSize: theme.old.components.mailFolderItem.fontSize - 1,
    color: theme.old.typography.colors.muted,
  },
  invisibleIcon: {
    visibility: 'hidden',
  },
  folderName: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  hoverMenu: {
    '& > span > a > div > button': {
      visibility: 'hidden',
    },
    '&:hover': {
      '& > span > a > div > button': {
        visibility: 'visible',
      },
    },
  },
  marginTop: {
    marginTop: theme.old.components.mailFolderItem.spacing,
  },
  projectNewsDotCollapsed: {
    '&::after': {
      content: "''",
      position: 'absolute',
      top: '50%',
      transform: 'translateY(-250%)',
      right: 6,
      width: 6,
      height: 6,
      backgroundColor: theme.colors.base.primary.default,
      borderRadius: '50%',
      display: 'none',
      zIndex: 10,
    },
    '&:not(:has([class^="separator"]))::after': {
      display: 'block',
    },
  },
  projectNewsDot: {
    height: 6,
    width: 6,
    backgroundColor: theme.colors.base.primary.default,
    borderRadius: '50%',
  },
}));

const isDescendantOf: (item: MailFolder, of: MailFolder) => boolean = (
  item: MailFolder,
  of: MailFolder
) => {
  if (!of?.childFolder) return false;
  for (const childFolder of of.childFolder) {
    if (childFolder.id === item.id) return true;
    if (childFolder.childFolder?.length > 0) {
      const isDescendant = isDescendantOf(item, childFolder);
      if (isDescendant) return true;
    }
  }
  return false;
};

const getIconName: (
  mailFolderId: MailFolderId,
  specialMailFolders: SpecialMailFolders
) => IconName = (mailFolderId, specialMailFolders) => {
  if (!mailFolderId || !specialMailFolders) return 'folder';
  switch (mailFolderId) {
    case specialMailFolders['inboxFolder']?.id:
      return 'inbox';
    case specialMailFolders['sendFolder']?.id:
      return 'paper-plane';
    case specialMailFolders['draftFolder']?.id:
      return 'pen';
    case specialMailFolders['deletedFolder']?.id:
      return 'trash';
    default:
      return 'folder';
  }
};

interface MailFolderMenuItemProps {
  projectId: ProjectId;
  mailFolder: MailFolder;
  key: MailFolderId;
  showNewFolderModal: (parentFolderId?: MailFolderId) => void;
  onDelete: (mailFolderId: MailFolderId, mailFolderName: string) => void;
  specialMailFolders: SpecialMailFolders;
  pathPrefix?: string;
  collapsed?: boolean;
  selectedMailFolderId?: string;
  clearSelectedMessages?: (messageIds: Message[]) => void;
  projectNews: ProjectNewsCategoryKey;
}

export const MailFolderMenuItem: React.FC<MailFolderMenuItemProps> = (
  props
) => {
  //#region ------------------------------ Defaults
  const {
    mailFolder,
    projectId,
    key,
    showNewFolderModal,
    onDelete,
    specialMailFolders,
    pathPrefix,
    collapsed,
    selectedMailFolderId,
    clearSelectedMessages,
    children,
    projectNews,
    ...rest
  } = props;

  const classes = useStyles();

  const { t } = useTranslation();
  const dispatch = useDispatch();
  const theme = useTheme<PrioTheme>();
  const renameFolderInputRef = useRef(null);
  //#endregion

  //#region ------------------------------ States / Attributes / Selectors
  const iconName = getIconName(mailFolder?.id, specialMailFolders);
  const displayName = getDisplayName(mailFolder, specialMailFolders, t);

  const mailFolderId = useMemo(() => mailFolder?.id, [mailFolder]);

  const isSubMenu = useMemo(() => !!mailFolder.childFolder, [mailFolder]);

  const [timer, setTimer] = useState(null);

  const mailSettings = useSelector(getMailSettings);

  const ultimateFilterRef =
    useContext<MutableRefObject<UltimateFilterSearchRef<MailSearchFormModel>>>(
      MailFilterContext
    );
  const [renameFolderModalOpen, setRenameFolderModalOpen] =
    useState<boolean>(false);
  const [newFolderName, setNewFolderName] = useState<string>('');
  const [renameFolderNameValid, setRenameFolderNameValid] =
    useState<boolean>(true);
  const [folderNameChanged, setFolderNameChanged] = useState<boolean>(false);
  const [folderRenaming, setFolderRenaming] = useState<boolean>(false);

  const unreadAssignments = useMemo(
    () =>
      (projectNews?.[
        'messageAssignmentUnread'
      ] as unknown as ProjectNewsDto[]) ?? [],
    [projectNews]
  );

  const hasUnreadAssignments = unreadAssignments.some((assignment) =>
    unreadAssignments.some(
      (assignment) => assignment.referenceObjectParentId === mailFolderId
    )
  );
  //#endregion

  //#region ------------------------------ Methods / Handlers
  const moveMail = (
    message: Message,
    selectedMessages: Message[],
    destinationId: MailFolderId,
    originId: MailFolderId,
    inboxFolderId: MailFolderId
  ) => {
    const selectedMessageIds = selectedMessages.map(
      (selectedMessage) => selectedMessage.id
    );
    const messages = selectedMessageIds.includes(message.id)
      ? selectedMessages
      : [message];
    dispatch(
      moveMessage(projectId, messages, destinationId, originId, inboxFolderId)
    );
    dispatch(fetchMessagesSagaAction(projectId, originId));
  };

  const moveFolder = async (
    mailFolderId: MailFolderId,
    destinationId: MailFolderId
  ) => {
    const { result } = await apiMoveMailFolder(
      projectId,
      mailFolderId,
      destinationId
    );
    if (result.status >= 200 && result.status < 300) {
      dispatch(fetchMailFolders(projectId));
    } else {
      notification.open({
        message: t('common:error'),
        description: t('mail:errorMessages.messages.moveMailFolderError'),
      });
    }
  };

  const handleDelete = () => {
    onDelete(mailFolder.id, mailFolder.displayName);
  };

  const closeRenameFolderModal = () => {
    setRenameFolderModalOpen(false);
    setNewFolderName('');
  };

  const onRenameFolderOk = async () => {
    const newDisplayName = newFolderName.trim();
    if (newDisplayName === '') {
      closeRenameFolderModal();
      return;
    }
    setFolderRenaming(true);
    dispatch(
      projectId === 'me'
        ? renameMailFolderMe(
            mailFolderId,
            newDisplayName,
            mailFolder.displayName
          )
        : renameMailFolder(
            projectId,
            mailFolderId,
            newDisplayName,
            mailFolder.displayName
          )
    );
    setFolderRenaming(false);
    closeRenameFolderModal();
  };

  const onRenameFolderInputChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    setNewFolderName(e.target.value);
    setFolderNameChanged(e.target.value !== mailFolder.displayName);
    setRenameFolderNameValid(!!e.target.value.match(folderNameRegex));
  };

  const handleRenameFolder = () => {
    setNewFolderName(mailFolder.displayName);
    setRenameFolderModalOpen(true);
  };

  const isSpecialFolder = useMemo(() => {
    if (!specialMailFolders || !mailFolder) return false;
    return Object.values(specialMailFolders)
      .filter((folder) => !!folder)
      .map((folder) => folder?.id)
      .includes(mailFolder?.id);
  }, [specialMailFolders, mailFolder]);
  //#endregion

  //#region ------------------------------ Effects
  useEffect(() => {
    if (renameFolderModalOpen && renameFolderInputRef.current) {
      renameFolderInputRef.current.select();
    }
  }, [renameFolderModalOpen]);
  //#endregion

  //#region ------------------------------ Drag & Drop
  const [{ canDrop, isOver }, dropRef] = useDrop({
    accept: [DND_TYPE_EMAIL, DND_TYPE_EMAIL_FOLDER],
    drop: (item, monitor) => {
      const type = monitor.getItemType();
      switch (type) {
        case DND_TYPE_EMAIL: {
          if (monitor.didDrop()) {
            return undefined;
          }
          if ((item as any).isPersonal && projectId !== 'me') {
            return {
              destinationMailFolder: mailFolder.id,
              message: (item as any).message as Message,
              projectId: projectId,
            };
          } else {
            if (clearSelectedMessages) {
              clearSelectedMessages([]);
            }
            const originId = ((item as any).message as Message).parentFolderId;
            moveMail(
              (item as any).message as Message,
              (item as any).selectedMessages as Message[],
              mailFolder.id === specialMailFolders['inboxFolder']?.id
                ? 'inbox'
                : mailFolder.id,
              specialMailFolders &&
                originId === specialMailFolders['inboxFolder']?.id
                ? 'inbox'
                : originId,
              specialMailFolders['inboxFolder']?.id
            );
            return undefined;
          }
        }
        case DND_TYPE_EMAIL_FOLDER: {
          if (monitor.didDrop()) {
            return undefined;
          }
          moveFolder(
            ((item as any).mailFolder as MailFolder).id,
            mailFolder.id
          );
          break;
        }
        default:
      }
      return undefined;
    },
    canDrop: (item) => {
      const target = mailFolder;
      const source = (item as any).mailFolder as MailFolder;
      return source?.id !== target.id && !isDescendantOf(target, source);
    },
    collect: (monitor: any) => ({
      isOver: monitor.isOver(),
      canDrop: monitor.canDrop(),
    }),
  });

  const [{ opacity }, dragRef] = useDrag({
    type: DND_TYPE_EMAIL_FOLDER,
    item: { mailFolder },
    collect: (monitor) => ({
      opacity: monitor.isDragging() ? 0.5 : 1,
    }),
  });

  const isActive = canDrop && isOver;
  //#endregion

  //#region ------------------------------ Components
  const menu = (
    <Menu>
      <Menu.Item key="delete" onClick={handleDelete}>
        {t('common:delete')}
      </Menu.Item>
      <Menu.Item
        key="newFolder"
        onClick={() => showNewFolderModal(mailFolder.id)}
      >
        {t('mail:mailNav.newFolder')}
      </Menu.Item>
      {!isSpecialFolder && (
        <Menu.Item key="renames" onClick={handleRenameFolder}>
          {t('common:actions.rename')}
        </Menu.Item>
      )}
    </Menu>
  );

  const link = (
    <Link
      to={`${pathPrefix ?? ''}../${
        specialMailFolders &&
        mailFolder.id === specialMailFolders['inboxFolder']?.id
          ? 'inbox'
          : mailFolder.id
      }`}
      onClick={(e) => {
        e.stopPropagation();
        dispatch(setMailListNavigationState(null, projectId));
        dispatch(deleteMailSearchResults(projectId === 'me'));
        ultimateFilterRef?.current?.setFilterValue({}, true);
        ultimateFilterRef?.current?.blur(true);
      }}
      ref={dropRef}
    >
      <Flex.Row alignItems="center">
        <FontAwesomeIcon
          fixedWidth
          icon={['fal', iconName]}
          className={classNames({
            [classes.invisibleIcon]: iconName === 'folder',
            [classes.icon]: !collapsed,
          })}
        />

        <Flex.Row
          flex={1}
          className={classes.folderName}
          childrenGap={theme.old.spacing.baseSpacing}
          alignItems="baseline"
        >
          <Flex.Row alignItems="center" flex={1} overflow={'hidden'}>
            <Flex.Item flex={1} overflow={'hidden'}>
              <div
                style={{
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
                title={displayName}
              >
                {displayName}
              </div>
            </Flex.Item>
            {hasUnreadAssignments && (
              <span className={classes.projectNewsDot}></span>
            )}
          </Flex.Row>
          {mailFolder.unreadItemCount && !collapsed ? (
            <span className={classes.unreadItemCount}>
              {mailFolder.unreadItemCount}
            </span>
          ) : null}
        </Flex.Row>

        {!collapsed && (
          <Dropdown
            overlay={menu}
            trigger={['click']}
            placement="bottomRight"
            destroyPopupOnHide
          >
            <Button
              iconProp={['fal', 'ellipsis-v']}
              onClick={(e) => e.stopPropagation()}
              type="link"
              className={classes.button}
            />
          </Dropdown>
        )}
      </Flex.Row>
    </Link>
  );
  //#endregion

  //#region ------------------------------ Effects
  useEffect(() => {
    if (isSubMenu && !collapsed && isOver) {
      setTimer(
        setTimeout(() => {
          dispatch(addOpenMailFolder(projectId, mailFolderId));
        }, 1000)
      );
    }
  }, [isOver, projectId, mailFolderId, isSubMenu, collapsed, dispatch]);

  useEffect(() => {
    if (!isOver && timer) {
      clearTimeout(timer);
    }
  }, [isOver, timer]);
  //#endregion

  return (
    <div
      className={classNames(collapsed ? classes.collapsedMenuItem : '')}
      style={{
        position: 'relative',
      }}
      key={key}
    >
      <div
        ref={dragRef}
        style={{ opacity }}
        className={classNames({
          [classes.projectNewsDotCollapsed]: hasUnreadAssignments && collapsed,
        })}
      >
        {mailFolder.childFolder && !collapsed ? (
          <Menu.SubMenu
            key={key}
            title={link}
            className={classNames(classes.subMenuItem, classes.hoverMenu, {
              [classes.subMenuItemCompressedView]:
                mailSettings.mailListSpacing === 'tight',
              [classes.subMenuItemMiddleView]:
                mailSettings.mailListSpacing === 'middle',

              [classes.activeMenuItem]: isActive,
              [classes.selectedSubMenu]: selectedMailFolderId === mailFolder.id,
            })}
            style={{
              ...(collapsed
                ? {}
                : {
                    fontSize: theme.old.components.mailFolderItem.fontSize,
                    lineHeight: theme.old.components.mailFolderItem.lineHeight,
                  }),
              padding: 0,
            }}
            {...rest}
          >
            {mailFolder.childFolder?.map((folder: MailFolder) => (
              <MailFolderMenuItem
                key={folder.id}
                mailFolder={folder}
                projectId={projectId}
                showNewFolderModal={showNewFolderModal}
                onDelete={onDelete}
                specialMailFolders={specialMailFolders}
                selectedMailFolderId={selectedMailFolderId}
                clearSelectedMessages={clearSelectedMessages}
                pathPrefix={pathPrefix}
                projectNews={projectNews}
              />
            ))}
          </Menu.SubMenu>
        ) : (
          <Menu.Item
            className={classNames(classes.menuItem, classes.hoverMenu, {
              [classes.activeMenuItem]: isActive,
              [classes.selectedMenuItem]:
                selectedMailFolderId === mailFolder.id,
            })}
            title={
              <Flex.Row alignItems="center">
                <FontAwesomeIcon
                  fixedWidth
                  icon={['fal', iconName]}
                  className={classNames(classes.icon, {
                    [classes.invisibleIcon]: iconName === 'folder',
                  })}
                />
                <Flex.Item flex={1} className={classes.folderName}>
                  {displayName}
                  {mailFolder.unreadItemCount ? (
                    <span> {mailFolder.unreadItemCount}</span>
                  ) : null}
                </Flex.Item>
              </Flex.Row>
            }
            style={{
              ...(collapsed
                ? {}
                : {
                    fontSize: theme.old.components.mailFolderItem.fontSize,
                    lineHeight: theme.old.components.mailFolderItem.lineHeight,
                  }),
            }}
            {...rest}
          >
            {link}
          </Menu.Item>
        )}
        <Modal
          title={t('mail:modals.rename.title')}
          visible={renameFolderModalOpen}
          onOk={onRenameFolderOk}
          onCancel={closeRenameFolderModal}
          confirmLoading={folderRenaming}
          okButtonProps={{
            disabled: !renameFolderNameValid || !folderNameChanged,
          }}
          okText={t('mail:modals.rename.okText')}
          cancelText={t('mail:modals.rename.cancelText')}
        >
          <div>
            <Input
              value={newFolderName}
              ref={renameFolderInputRef}
              onChange={onRenameFolderInputChange}
              className={classes.marginTop}
            />
            <Alert
              message={t('mail:modals.rename.invalidFileName')}
              type="error"
              style={{
                visibility: renameFolderNameValid ? 'hidden' : 'visible',
              }}
              className={classes.marginTop}
            />
          </div>
        </Modal>
      </div>
    </div>
  );
};

export default MailFolderMenuItem;
